import {
  OmniElement,
  OmniStyleElement,
  html,
  css,
  ref,
  createRef,
  repeat,
  nothing,
  classMap,
} from 'omni-ui';

import CentralizedDataAPI from '../../services/centralized-data-api.js';

OmniStyleElement.register();

/**
 * @description CampaignAudienceVizDetailView is a component that renders the detail view and the table of CampaignAudienceViz
 * @element audience-viz-detail-view
 * @extends {OmniElement}
 */
export class CampaignAudienceVizDetailView extends OmniElement {
  /**
   * @description Reactive properties of the class
   */
  static get properties() {
    return {
      audiences: { type: Array },
      stages: { type: Array },
      isCluster: { type: Boolean },
      funnelType: { type: String },
      readOnly: { type: Boolean },
      context: { type: Object },
      selectedAEObject: { type: Array },
    };
  }

  /**
   * @description CSS styles for the component CampaignAudienceVizDetailView
   */
  static get styles() {
    return css`
      :host {
        display: flex;
        flex-flow: column nowrap;
        flex: 1 1 100%;
        min-height: 0;
      }
      omni-style {
        display: flex;
        flex-flow: column nowrap;
        flex: 1 1 100%;
        gap: 1.71rem;
        min-height: 0;
      }
      [hidden] {
        display: none !important;
      }
      .omni div.table-container {
        display: flex;
        flex-flow: row nowrap;
        gap: 1.71rem;
        flex: 1 1 100%;
        min-height: 0;
        margin-bottom: 0;
      }
      div.table-container button.is-outlined {
        justify-self: start;
        height: 2.36rem;
      }
      div.table-container button.audience {
        margin-top: 2rem;
      }
      div.detail-table {
        display: flex;
        flex-flow: row nowrap;
        overflow-x: auto;
        overflow-y: auto;
      }
      div.detail-table::-webkit-scrollbar {
        width: 0.57rem;
        height: 0.57rem;
      }
      div.detail-table::-webkit-scrollbar-thumb {
        background-color: rgba(0, 0, 0, 0.2);
        border-radius: 0.29rem;
      }

      div.detail-table::-webkit-scrollbar-track {
        background-color: rgba(0, 0, 0, 0.1);
      }

      div.table-column {
        display: flex;
        flex-flow: column nowrap;
      }
      div.cell {
        display: flex;
        flex-direction: column;
        width: 20rem;
        min-height: 11.11rem;
        padding: 1.71rem;
        border-right: thin solid rgba(var(--rgb-core-medium), 0.1);
        border-bottom: thin solid rgba(var(--rgb-core-medium), 0.1);
      }
      div.cell button.is-text {
        align-self: flex-end;
        min-height: 2.36rem;
      }
      div.audience-cell {
        padding-top: 0;
      }
      div.stage-cell {
        padding-left: 0;
      }
      div.radio-cell {
        padding-top: 0;
        padding-left: 0;
      }
      .omni div.radio-cell label.radio {
        color: rgba(var(--rgb-core-dark), 0.78);
      }
      div.percent-warning {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin: 0.5rem 0 0 0.5rem;
      }
      div.percent-warning omni-icon {
        --color-icon-lines: var(--color-error);
      }
      div.score-container {
        position: relative;
        display: flex;
        flex-flow: row nowrap;
        align-items: baseline;
        margin-right: 12.06rem;
      }
      omni-tile.funnel-type-popup {
        position: absolute;
        bottom: calc(100% + 0.71rem);
        left: 0;
        z-index: 1;
        width: 28.57rem;
      }
      omni-tile.funnel-type-popup p[slot='header'] {
        padding: 0.71rem 1.14rem;
      }
      omni-tile div.popup-body {
        display: flex;
        flex-direction: column;
        flex-wrap: nowrap;
        justify-content: flex-start;
        padding: 1.14rem 2.14rem 2.14rem 1.14rem;
        gap: 2.14rem;
      }
      div.popup-body div.popup-buttons {
        display: flex;
        justify-content: flex-end;
        gap: 0.71rem;
      }
      div.score-cell {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 20rem;
        padding: 0 2.39rem;
      }
      div.score-cell.stage {
        padding-left: 0;
      }
      div.scores {
        display: flex;
        overflow-x: hidden;
        flex: 1 1 100%;
      }
      div.score-cell label.label {
        color: var(--color-core-dark);
        font-weight: 600;
      }
      div.footer-container {
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex: 0 0 auto;
      }
      div.footer-tooltip {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 1rem;
      }
      div.footer-container button.visualization {
        height: 2.36rem;
      }
    `;
  }

  /**
   * @property {Object[]} audiences
   * @property {Object[]} stages
   * @property {String} funnelType - type of funnel visualization
   * @property {Boolean} isCluster - true if the funnel has less than 2 stages, false otherwise
   * @property {Boolean} readOnly - true if the component is read only, false otherwise
   * @property {Object} tableRef - reference to the table container
   * @property {Object} scoreRef - reference to the score container
   * @property {Boolean} isSyncingTable - true if the table is syncing the horizontal scroll to the score, false otherwise
   * @property {Boolean} isSyncingScore - true if the score is syncing the horizontal scroll to the table, false otherwise
   * @property {Boolean} validNumber - true if the percentages of the stages add up to 100 if the funnel type is 'stacked'. If the funnel type is 'pipeline', true if the percentages are in descending order and the first percentage is 100.
   */
  constructor() {
    super();
    this.audiences = [];
    this.stages = [];
    this.selectedAEObject = [];
    this.inValidAudienceDetails = false;
    this.funnelType = null;
    this.isCluster = true;
    this.readOnly = false;
    this.tableRef = createRef();
    this.scoreRef = createRef();
    this.isSyncingTable = false;
    this.isSyncingScore = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this.objectsAPI = new CentralizedDataAPI(this.token, this.env);
  }

  /**
   * @description Adds event listeners to the table and score containers to sync their horizontal scroll
   */
  firstUpdated() {
    this.tableRef.value.addEventListener('scroll', () => {
      this.scrollListener('table');
    });
    this.scoreRef.value.addEventListener('scroll', () => {
      this.scrollListener('score');
    });
  }

  /**
   * @description Search for objects in audience category that match the search term
   * @param {String} search - search term input by the user in the dropdown
   * @param {Number} audienceIndex - index of the audience dropdown
   * @returns {Object[]} audiences - array of audience objects
   */
  #getAudienceObjects = async (search, audienceIndex) => {
    const { clientId } = this.context;
    let audiences = [];
    try {
      const { data: results = [] } = await this.objectsAPI.getSharedObjects({
        // audience category
        category: '5c2869a9-1802-4526-bc19-94561f8279b7',
        name: search,
        client: clientId,
        length: '500',
      });
      audiences = results
        .map(result => {
          const dataPoints = Array.isArray(result.data_points)
            ? result.data_points.reduce((agg, dp) => ({ ...agg, ...dp }))
            : result.data_points;
          return {
            name: result.name,
            uuid: result.uuid_object,
            uuid_api_connection: result.uuid_api_connection,
            data_points: dataPoints,
            subcategory: result.sub_category,
          };
        })
        .filter(
          // Only use audiences with these data points
          object =>
            object.data_points.id_count && object.data_points.pop_id_count,
        )
        .filter(
          // filter out audience objects that have the same uuid as a selected
          // object in another dropdown
          object =>
            !Object.entries(this.selectedAEObject).some(
              ([dropdownIndex, key]) =>
                dropdownIndex !== String(audienceIndex) && object.uuid === key,
            ),
        );
    } catch (error) {
      console.error('Error getting audience objects:', error);
    }

    // Group the audiences by subcategory and use that to render the dropdown
    const groupedAudiences = [];
    const subcategories = {};
    audiences.forEach(audience => {
      subcategories[audience.subcategory.uuid_category] =
        audience.subcategory.name;
    });
    Object.entries(subcategories).forEach(
      ([subcategoryId, subcategoryName]) => {
        const children = audiences.filter(
          audience => audience.subcategory.uuid_category === subcategoryId,
        );
        groupedAudiences.push({
          name: subcategoryName,
          uuid: subcategoryId,
          selectable: false,
          children,
        });
      },
    );

    return groupedAudiences;
  };

  /**
   * @description Syncs the horizontal scroll of the table and score elements
   * @param {string} elementToSync - The element to sync the scroll position to ('table' or 'score')
   */
  scrollListener = elementToSync => {
    const isSyncingTable = elementToSync === 'table';
    const isSyncingScore = elementToSync === 'score';

    if (!isSyncingTable && !isSyncingScore) {
      return;
    }

    if (isSyncingTable && !this.isSyncingScore) {
      this.isSyncingTable = true;
      this.scoreRef.value.scrollLeft = this.tableRef.value.scrollLeft;
    }

    if (isSyncingScore && !this.isSyncingTable) {
      this.isSyncingScore = true;
      this.tableRef.value.scrollLeft = this.scoreRef.value.scrollLeft;
    }

    this.isSyncingTable = false;
    this.isSyncingScore = false;
  };

  /**
   * @description Dispatches a new event with the index of the stage to delete and resets the funnel type and radio buttons if there is less then 2 stages
   * @param {number} index - index of the stage to delete
   */
  deleteStage = stageIndex => {
    this.dispatchNewEvent('delete-stage', {
      detail: {
        stageIndex,
      },
    });
  };

  /**
   * @param {object} audience - audience object
   * @param {number} audienceIndex - index of the audience
   * @returns {TemplateResult} - HTML template for the audience dropdown
   */

  #audienceDropDownTemplate = (audience, audienceIndex) => {
    const disableDeleteAudience = this.audiences.length <= 1;
    return html`
      <div class="cell audience-cell">
        <div class="field">
          <label class="label is-size-7">Audience ${audienceIndex + 1}</label>
          <div class="control">
            <om2-dropdown
              hasSearch
              .asyncSearchTree="${async search =>
                this.#getAudienceObjects(search, audienceIndex)}"
              hasObjectValues
              placeholder="Select audience"
              idProperty="uuid"
              nameProperty="name"
              .value=${audience}
              @change=${e => {
                if (e.detail.value === audience) return;
                this.dispatchNewEvent('audience-change', {
                  detail: {
                    audienceIndex,
                    value: e.detail.value,
                  },
                });
              }}
            ></om2-dropdown>
          </div>
        </div>
        <button
          class="button is-text"
          @click=${() => {
            this.dispatchNewEvent('delete-audience', {
              detail: {
                audienceIndex,
              },
            });
          }}
          ?disabled=${disableDeleteAudience}
        >
          Delete audience
        </button>
      </div>
    `;
  };

  /**
   * @param {object} stage - stage object
   * @param {number} stageIndex - index of the stage
   * @returns {TemplateResult} - HTML template for the stage input
   */
  #stageInputTemplate = (stage, stageIndex) => {
    const disableDeleteStage = this.stages.length <= 1;
    return html`
      <div class="cell stage-cell">
        <div class="field">
          <label class=" label is-size-7">Stage ${stageIndex + 1}</label>
          <div class="control">
            <input
              .value=${stage.name}
              class="input"
              placeholder="Enter stage title"
              @change=${e => {
                this.dispatchNewEvent('stage-change', {
                  detail: {
                    stageIndex,
                    value: e.target.value,
                  },
                });
              }}
            />
          </div>
        </div>
        <button
          class="button is-text"
          @click=${() => this.deleteStage(stageIndex)}
          ?disabled=${disableDeleteStage}
        >
          Delete Stage
        </button>
      </div>
    `;
  };

  /**
   * @param {string} percent - percentage value
   * @param {number} percentIndex - index of the percentage
   * @param {number} audienceIndex - index of the audience the percentage belongs to
   * @returns {TemplateResult} - HTML template for the percentage input
   */
  #percentageInputTemplate = (percent, percentIndex, audienceIndex) => {
    let warning = nothing;
    if (percent.invalid) {
      warning = html`
        <div class="percent-warning">
          <omni-icon
            class="is-size-6"
            icon-id="omni:informative:info"
          ></omni-icon>
          <p class="is-size-7">${percent.invalid}</p>
        </div>
      `;
    }
    return html`
      <div class="cell">
        <div class="field">
          <label class=" label is-size-7">Percentage</label>
          <div class="control">
            <input
              ?disabled=${this.stages.length === 1}
              .value=${percent.value !== null ? `${percent.value}%` : ''}
              class=${classMap({
                input: true,
                'is-danger': !!percent.invalid,
              })}
              placeholder="Enter percentage"
              @change=${e => {
                const { value } = e.target;
                this.dispatchNewEvent('percentage-change', {
                  detail: {
                    percentIndex,
                    audienceIndex,
                    value,
                  },
                });
              }}
            />
            ${warning}
          </div>
        </div>
      </div>
    `;
  };

  /**
   * @returns {TemplateResult} - HTML template for the radio buttons
   */
  #radioBtnsTemplate = () => {
    const { visualization } = this.schema;
    return html`
      <div class="field" ?hidden=${this.stages.length <= 1}>
        <label class="label is-size-7">
          Funnel visualization type
          <omni-tooltip>
            <omni-icon
              slot="invoker"
              class="is-size-2"
              icon-id="omni:informative:info"
            ></omni-icon>
            <div slot="content">${visualization.info}</div>
          </omni-tooltip>
        </label>
        <div class="control">
          <label class="label radio">
            <input
              id="radio-pipeline"
              type="radio"
              name="funnelType"
              value="pipeline"
              ?checked=${this.funnelType === 'pipeline'}
              @change=${e => {
                this.dispatchNewEvent('radio-change', {
                  detail: {
                    value: e.target.value,
                  },
                });
              }}
            />
            Pipeline
          </label>
          <label class="label radio">
            <input
              id="radio-stacked"
              type="radio"
              name="funnelType"
              value="stacked"
              ?checked=${this.funnelType === 'stacked'}
              @change=${e => {
                this.dispatchNewEvent('radio-change', {
                  detail: {
                    value: e.target.value,
                  },
                });
              }}
            />
            100% Stacked
          </label>
        </div>
      </div>
    `;
  };

  /**
   * @returns {TemplateResult} - HTML template for the detail table
   */
  #detailTableTemplate = () => html`
    <div class="detail-table" ${ref(this.tableRef)}>
      <div class="table-column">
        <div class="cell radio-cell">${this.#radioBtnsTemplate()}</div>
        ${repeat(
          this.stages,
          stage => stage.name,
          (stage, stageIndex) => html`
            ${this.#stageInputTemplate(stage, stageIndex)}
          `,
        )}
      </div>
      ${repeat(
        this.audiences,
        audience => audience.id,
        (audience, audienceIndex) => html`
          <div class="table-column">
            ${this.#audienceDropDownTemplate(audience, audienceIndex)}
            ${repeat(
              audience.stagePercentages,
              (percent, index) => `${percent.value}-${index}`,
              (percent, percentageIndex) => html`
                ${this.#percentageInputTemplate(
                  percent,
                  percentageIndex,
                  audienceIndex,
                )}
              `,
            )}
          </div>
        `,
      )}
    </div>
  `;

  #scoreSumTemplate = audience => {
    const sum = audience.stagePercentages.reduce(
      (acc, curr) => acc + (curr.value || 0),
      0,
    );
    return html`
      <div class="score-cell">
        ${this.funnelType === 'stacked'
          ? html`
              <p
                class="${classMap({
                  'has-text-danger': sum !== 100,
                })}"
              >
                ${sum}%
              </p>
            `
          : nothing}
      </div>
    `;
  };

  /**
   * @returns {TemplateResult} - HTML template for the component CampaignAudienceVizDetailView
   */
  // TODO: break out sections of HTML to their own template functions.
  render() {
    const { footer } = this.schema;
    return html`
      <omni-style>
        <div class="table-container">
          ${this.#detailTableTemplate()}

          <button
            class="button is-outlined audience"
            @click=${() => this.dispatchNewEvent('add-audience')}
            ?disabled=${this.audiences.length >= 5}
          >
            <omni-icon icon-id="omni:interactive:plus"></omni-icon>
            <span>Audience</span>
          </button>
        </div>

        <div class="score-container">
          <div class="score-cell stage">
            <button
              class="button is-outlined stage"
              @click=${async () => {
                this.dispatchNewEvent('add-stage');
                await this.updateComplete;
              }}
              ?disabled=${this.stages.length >= 5}
            >
              <omni-icon icon-id="omni:interactive:plus"></omni-icon>
              <span>Stage</span>
            </button>
            <div
              ?hidden=${this.funnelType === null ||
              this.funnelType === 'pipeline'}
            >
              <label class="label">Total:</label>
            </div>
          </div>
          <div class="scores" ${ref(this.scoreRef)}>
            ${repeat(
              this.audiences,
              audience => audience.id,
              audience => this.#scoreSumTemplate(audience),
            )}
          </div>
        </div>

        <div class="footer-container">
          <div class="footer-tooltip">
            <omni-icon
              slot="invoker"
              class="is-size-2"
              icon-id="omni:informative:info"
            ></omni-icon>
            <p>${footer.info}</p>
          </div>
          <button
            class="button is-primary visualization"
            ?disabled=${this.inValidAudienceDetails}
            @click=${() => {
              this.dispatchNewEvent('create-visualization');
            }}
          >
            ${this.isCluster ? footer.button.cluster : footer.button.funnel}
          </button>
        </div>
      </omni-style>
    `;
  }
}

OmniElement.register(
  'campaign-audience-viz-detail-view',
  CampaignAudienceVizDetailView,
);
